// src/utils/deviceApi.js
const BASE_URL = process.env.REACT_APP_BACKEND_URL + "/api";

/**
 * Helper function to get the Authorization header with the JWT token.
 * Returns an empty object if no token is found.
 */
const getAuthHeader = () => {
    const token = localStorage.getItem('piwatch_token');
    if (!token) {
        console.warn("No authentication token found in localStorage.");
        return {};
    }
    return { 'Authorization': `Bearer ${token}` };
};

/**
 * Helper function to handle potential 401 Unauthorized responses.
 * If 401, removes the token and reloads the page to force login.
 * Returns true if unauthorized, false otherwise.
 */
const handleUnauthorized = (response) => {
    if (response.status === 401) {
        console.error("⛔ Unauthorized (401): Token might be invalid or expired. Logging out.");
        localStorage.removeItem('piwatch_token');
        window.location.reload(); // Force login
        return true; // Indicates unauthorized, stop further processing
    }
    return false; // Authorized or other error
};

/**
 * Fetches the list of devices from the backend.
 * Includes the JWT token in the request header.
 * Returns an empty array on error or if unauthorized.
 */
export async function fetchDevices() {
  try {
    const headers = {
        ...getAuthHeader(), // Get the Authorization header
        'Content-Type': 'application/json' // Keep content type for consistency
    };
    console.log("Fetching devices with headers:", headers); // Debug log
    const res = await fetch(`${BASE_URL}/devices`, { headers: headers });

    if (handleUnauthorized(res)) {
        return []; // Stop processing if unauthorized
    }

    if (!res.ok) {
        // Log other non-401 errors
        console.error(`Fetch devices failed with status: ${res.status}`);
        const errorBody = await res.text(); // Read error body if possible
        console.error("Error body:", errorBody);
        throw new Error(`HTTP error ${res.status}`);
    }

    const data = await res.json();
    console.log("Devices received:", data); // Debug log
    // Ensure the response is actually an array
    return Array.isArray(data) ? data : [];
  } catch (err) {
    console.error("❌ Fetch devices error:", err);
    return []; // Return empty array on any exception
  }
}

/**
 * Adds a new device via the backend API.
 * Includes the JWT token in the request header.
 * Returns the response JSON on success, null on error.
 */
export async function addDevice(device) {
  try {
    const headers = {
        ...getAuthHeader(), // Get the Authorization header
        'Content-Type': 'application/json'
    };
    console.log("Adding device with headers:", headers, "and body:", device); // Debug log
    const res = await fetch(`${BASE_URL}/devices`, {
      method: "POST",
      headers: headers,
      body: JSON.stringify(device),
    });

    if (handleUnauthorized(res)) {
        return null; // Stop processing if unauthorized
    }

    const result = await res.json(); // Read JSON regardless of status first
    if (!res.ok) {
        console.error(`Add device failed with status: ${res.status}`, result);
        throw new Error(result.error || `HTTP error ${res.status}`);
    }
    console.log("Add device response:", result); // Debug log
    return result; // Return the success response (might contain updated list)
  } catch (err) {
    console.error("❌ Add device error:", err);
    alert(`Error adding device: ${err.message}`); // Show error to user
    return null;
  }
}

/**
 * Deletes a device via the backend API.
 * Includes the JWT token in the request header.
 * Returns the response JSON on success, null on error.
 */
export async function deleteDevice(device_id) {
  try {
    const headers = { ...getAuthHeader() }; // Get the Authorization header
    console.log(`Deleting device ${device_id} with headers:`, headers); // Debug log
    const res = await fetch(`${BASE_URL}/devices/${device_id}`, {
        method: "DELETE",
        headers: headers
    });

    if (handleUnauthorized(res)) {
        return null; // Stop processing if unauthorized
    }

    const result = await res.json(); // Read JSON regardless of status
    if (!res.ok) {
         console.error(`Delete device failed with status: ${res.status}`, result);
         throw new Error(result.message || `HTTP error ${res.status}`);
    }
    console.log("Delete device response:", result); // Debug log
    return result;
  } catch (err) {
    console.error("❌ Delete device error:", err);
     alert(`Error deleting device: ${err.message}`); // Show error to user
    return null;
  }
}